import { PrkOneInstance } from '../prk-one-instance';
import { EasingFunctions } from './../../utils/easingFunctions';
import { logError } from './../../utils/logError';
import rAF from './../../utils/rAF';
import cAF from './../../utils/cAF';
import { throttle } from '../../decorators';

export class PrkCollapsible extends PrkOneInstance {

    static HEADER_CLASS = 'prk-collapsible__header';
    static CONTENT_CLASS = 'prk-collapsible__content';
    static IS_INITIALIZED_CLASS = 'is-initialized'
    static IS_LOADING_CONTENT_CLASS = 'is-loading-content';

    static ASYNC_CONTENT_DATA_ATTRIBUTE = 'asyncContent'

    static IS_EXPANDED_ATTRIBUTE = 'aria-expanded';

    static DEFAULT_OPTIONS = {
        collapsedHeight: 0,
        expandedByDefault: true,
        onBeforeCollapse: null,
        onCollapsed: null,
        onBeforeExpand: null,
        onExpanded: null,
        animationDuration: 300,
        easingFunction: EasingFunctions.easeInOutCubic
    };

    /**
     * @protected
     * @var {HTMLElement}
     */
    header;

    /**
     * @protected
     * @var {HTMLElement}
     */
    content;

    /**
     * @protected
     * @var {any}
     */
    options;

    /**
     * @protected
     * @var {{ [eventListenerName: string]: EventListener }}
     */
    eventListeners;

    /**
     * @protected
     * @var {Function|null}
     */
    cancelAnimation = null;

    /**
     * @protected
     * @var {any}
     */
    frameHandle;

    /**
     * @protected
     * @var {boolean}
     */
    isContentLoaded;

    /**
     * @protected
     * @var {Promise<void>|null}
     */
    asyncContentPromise = null;

    /**
     * @public
     * @param {HTMLElement} element
     * @param {{
     *  animationDuration?: number,
     *  collapsedHeight?: number | () => number, // Should return height of collapsed state widget
     *  expandedByDefault?: boolean,
     *  onBeforeCollapse:? null | (next: Function) => void, // next should be called from within callback to continue
     *  onCollapsed?: null | () => void,
     *  onBeforeExpand?: null | (next: Function) => void, // next should be called from within callback to continue
     *  onExpanded?: nul l |() => void,
     *  easingFunction?: number => number
     * }|undefined} options
     */
    constructor (element, options) {
        super(element);
        if (this.isInitialized) return this.instance;
        
        this.options = Object.assign({}, PrkCollapsible.DEFAULT_OPTIONS, options);
        this.eventListeners = {
            onToggle: this.toggle.bind(this),
            onWndResize: throttle.call(this, this.handleWindowResized, 100),
        };

        this.init();
    }

    /**
     * @protected
     */
    init () {
        if (!this.element.hasAttribute(PrkCollapsible.IS_EXPANDED_ATTRIBUTE))
            this.element.setAttribute(PrkCollapsible.IS_EXPANDED_ATTRIBUTE, this.options.expandedByDefault ? 'true' : 'false');
            
        this.header = this.element.querySelector(`.${PrkCollapsible.HEADER_CLASS}`);
        this.header.setAttribute('role', 'button');

        this.content = this.element.querySelector(`.${PrkCollapsible.CONTENT_CLASS}`);

        if (this.content) {
            if (this.isExpanded) {
                this.content.style.height = '';
                this.content.style.overflow = 'visible';
            } else {
                const collapsedHeight = this.options.collapsedHeight instanceof Function
                    ? this.options.collapsedHeight()
                    : this.options.collapsedHeight;
                this.content.style.height = `${collapsedHeight}px`;
                this.content.style.overflow = '';
            }
        }

        this.isContentLoaded = !this.element.dataset[PrkCollapsible.ASYNC_CONTENT_DATA_ATTRIBUTE];

        this.attachEvents();
        this.isInitialized = true;
        this.element.classList.add(PrkCollapsible.IS_INITIALIZED_CLASS);
    }

    /**
     * @protected
     */
    attachEvents () {
        this.header.addEventListener('click', this.eventListeners.onToggle);
        window.addEventListener('resize', this.eventListeners.onWndResize);
    }

    /**
     * @protected
     */
    detachEvents () {
        this.header.removeEventListener('click', this.eventListeners.onToggle);
        window.removeEventListener('resize', this.eventListeners.onWndResize);
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isExpanded () {
        return this.element.hasAttribute(PrkCollapsible.IS_EXPANDED_ATTRIBUTE) && this.element.getAttribute(PrkCollapsible.IS_EXPANDED_ATTRIBUTE) === 'true';
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isCollapsed () {
        return !this.isExpanded;
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isOpen () {
        return this.isExpanded;
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isClosed () {
        return this.isCollapsed;
    }

    /**
     * @public
     * @returns {PrkCollapsible}
     */
    toggle () {
        return this.isExpanded
            ? this.collapse()
            : this.expand();
    }

    /**
     * @private
     * @returns {void}
     */
    handleWindowResized() {
        if (!this.isCollapsed)
            return;
        
        if (!(this.options.collapsedHeight instanceof Function))
            return;

        const collapsedHeight = this.options.collapsedHeight();
        this.content.style.height = `${collapsedHeight}px`;
    }

    /**
     * @private
     * @returns {{ currentHeight: number, maxHeight: number }}
     */
    getContentHeight () {
        const maxHeight = this.content.scrollHeight || 1; // 1 is to prevent division by 0, just in case
        const currentHeight = parseFloat(this.content.style.height);
        return { currentHeight: isNaN(currentHeight) ? maxHeight : currentHeight, maxHeight };
    }

    /**
     * @private
     * @param {number} startHeight
     * @param {number} endHeight
     * @param {number} duration
     * @returns {Promise<void>}
     */
    animateContentHeight (startHeight, endHeight, duration) {
        // If another animation is in progress, cancel it
        if (this.cancelAnimation) {
            cAF(this.frameHandle);
            this.cancelAnimation();
        }

        return new Promise((resolve, reject) => {
            let start, progress, newHeight;
            this.cancelAnimation = reject;

            const animate = () => {
                if (!start) start = Date.now();
                progress  = Math.min(this.options.easingFunction((Date.now() - start) / duration), 1);
                newHeight = startHeight * (1 - progress) + endHeight * progress;
                this.content.style.height = `${newHeight}px`;

                if (1 === progress)
                    resolve();
                else
                    this.frameHandle = rAF(animate);
            }

            this.frameHandle = rAF(animate);
        });
    }

    /**
     * @public
     * @returns {PrkCollapsible}
     */
    async collapse () {
        if (!this.content) return;

        // Setting attribute animates the arrow by css
        this.element.setAttribute(PrkCollapsible.IS_EXPANDED_ATTRIBUTE, 'false');

        try {
            $(this.element).trigger('before-collapse', { collapsible: this });
            if (this.options.onBeforeCollapse instanceof Function)
                await new Promise(resolve => this.options.onBeforeCollapse.call(this, resolve));

            const collapsedHeight = this.options.collapsedHeight instanceof Function
                ? this.options.collapsedHeight()
                : this.options.collapsedHeight;

            const { currentHeight, maxHeight } = this.getContentHeight();
            const duration = this.options.animationDuration * (currentHeight - collapsedHeight) / maxHeight;

            rAF(() => this.content.style.overflow = '');
            await this.animateContentHeight(currentHeight, collapsedHeight, duration);
            this.cancelAnimation = null;
            this.frameHandle     = null;

            $(this.element).trigger('collapsed', { collapsible: this });
            if (this.options.onCollapsed instanceof Function)
                this.options.onCollapsed.call(this);
        } catch (error) {
            if (error) logError(error);
        }
    }

    /**
     * @public
     * @returns {PrkCollapsible}
     */
    async expand () {
        if (!this.content) return;
        
        // Setting attribute animates the arrow by css
        this.element.setAttribute(PrkCollapsible.IS_EXPANDED_ATTRIBUTE, 'true');


        try {
            // Load async content if needed
            if (!this.isContentLoaded)
                await this.loadContent();

            $(this.element).trigger('before-expand', { collapsible: this });
            if (this.options.onBeforeExpand instanceof Function)
                await new Promise(resolve => this.options.onBeforeExpand.call(this, resolve));

            const { currentHeight, maxHeight } = this.getContentHeight();
            const duration = this.options.animationDuration * (maxHeight - currentHeight) / maxHeight;

            await this.animateContentHeight(currentHeight, maxHeight, duration);
            this.cancelAnimation = null;
            this.frameHandle     = null;
            rAF(() => {
                this.content.style.height = '';
                this.content.style.overflow = 'visible';
            }); // No need for height to be set when fully expanded

            $(this.element).trigger('expanded', { collapsible: this });
            if (this.options.onExpanded instanceof Function)
                this.options.onExpanded.call(this);
        } catch (error) {
            if (error) logError(error);
        }
    }

    /**
     * Loads async content
     * 
     * @protected
     */
    loadContent () {
        // If hasn't tryed to load async contnet yet
        if (null === this.asyncContentPromise) {
            // Add spinner into header and change class
            // to show spinner and hide arrow icon
            const spinner = this.renderSpinner();
            this.header.appendChild(spinner);
            this.element.classList.add(PrkCollapsible.IS_LOADING_CONTENT_CLASS);

            // Load and render async content
            this.asyncContentPromise = fetch(this.element.dataset[PrkCollapsible.ASYNC_CONTENT_DATA_ATTRIBUTE])
                .then(response => response.json())
                .then(data => this.content.innerHTML = data.html)
                .catch(logError)
                .then(() => {
                    // Remove spinner, show arrow icon
                    spinner.remove();
                    this.element.classList.remove(PrkCollapsible.IS_LOADING_CONTENT_CLASS);
                    this.isContentLoaded = true;
                });
        }

        return this.asyncContentPromise
    }

    /**
     * Returns a spinner to add to the DOM
     * @protected
     */
    renderSpinner () {
        const spinner = document.createElement('div');
        spinner.classList.add('sk-circle', 'prk-collapsible__spinner');

        for (let i = 1; i <= 12; i++) {
            const spinnerChild = document.createElement('div');
            spinnerChild.classList.add(`sk-circle${i}`, 'sk-child');
            spinner.appendChild(spinnerChild);
        }

        return spinner;
    }

    /**
     * Destroys PrkCollapsible instance
     * @public
     */
    destroy () {
        this.detachEvents();
        super.destroy();
        this.isInitialized = false;
        this.element.classList.remove(PrkCollapsible.IS_INITIALIZED_CLASS);
    }
}

export default PrkCollapsible;
